import gql from 'graphql-tag';

export const CREATE_PATIENT = gql`
  mutation createPatient(
    $firstName: String!
    $lastName: String!
    $gender: String!
    $pronoun: String
    $birthdate: String!
    $phone: String!
    $email: String!
    $authComms: Boolean
    $cognitoId: String!
    $federatedId: String
    $confirmed: Boolean
    $labId: Int
    $event: String
    $address: AddressInput
    $referralSource: String
  ) {
    createPatient(
      firstName: $firstName
      lastName: $lastName
      gender: $gender
      pronoun: $pronoun
      birthday: $birthdate
      phone: $phone
      email: $email
      authComms: $authComms
      cognitoId: $cognitoId
      federatedId: $federatedId
      confirmed: $confirmed
      labId: $labId
      event: $event
      address: $address
      referralSource: $referralSource
    ) {
      patient {
        id
        cognitoId
        email
      }
    }
  }
`;

export const UPDATE_PATIENT_EMAIL_CONFIRMED = gql`
  mutation updatePatient($confirmed: Boolean) {
    updatePatient(confirmed: $confirmed) {
      patient {
        id
      }
    }
  }
`;

export const CREATE_APPOINTMENT = gql`
  mutation createAppointment(
    $timeSlotId: Int!
    $appointmentTypeId: Int!
    $stripeTokenId: String
    $patientPackageId: Int
    $packageItemId: Int
    $discountCode: String
  ) {
    createAppointment(
      timeSlotId: $timeSlotId
      appointmentTypeId: $appointmentTypeId
      stripeTokenId: $stripeTokenId
      patientPackageId: $patientPackageId
      packageItemId: $packageItemId
      discountCode: $discountCode
    ) {
      appointment {
        id
        patientPackageId
        cancelable
        reschedulable
        appointmentType {
          virtual
          name
          description
          videoLink
          rateCents
          id
          category {
            id
            name
          }
        }
        location {
          id
          name
          address
          patientFacing
        }
        startTime
        endTime
        timeZone
      }
    }
  }
`;

export const CANCEL_APPOINTMENT_W_REASON = gql`
  mutation cancelAppointment($appointmentId: Int!, $reason: String!, $note: String) {
    cancelAppointment(appointmentId: $appointmentId, reason: $reason, note: $note) {
      appointment {
        id
        patientPackageId
        appointmentType {
          id
          virtual
          name
          description
          videoLink
          rateCents
          category {
            id
            name
          }
        }
        location {
          id
          name
          address
        }
        startTime
        endTime
        timeZone
      }
    }
  }
`;

export const CANCEL_APPOINTMENT = gql`
  mutation cancelAppointment($appointmentId: Int!) {
    cancelAppointment(appointmentId: $appointmentId) {
      appointment {
        id
        patientPackageId
        appointmentType {
          id
          virtual
          name
          description
          videoLink
          rateCents
          category {
            id
            name
          }
        }
        location {
          id
          name
          address
        }
        startTime
        endTime
        timeZone
      }
    }
  }
`;

export const RESCHEDULE_APPOINTMENT = gql`
  mutation rescheduleAppointment($appointmentId: Int!, $newTimeSlotId: Int!) {
    rescheduleAppointment(appointmentId: $appointmentId, newTimeSlotId: $newTimeSlotId) {
      appointment {
        id
        patientPackageId
        cancelable
        reschedulable
        appointmentType {
          id
          virtual
          name
          description
          videoLink
          rateCents
          category {
            id
            name
          }
        }
        location {
          id
          name
          address
        }
        startTime
        endTime
        timeZone
      }
    }
  }
`;

export const PURCHASE_PACKAGE = gql`
  mutation createPatientPackage($packageId: Int!, $stripeTokenId: String!, $discountCode: String) {
    createPatientPackage(
      packageId: $packageId
      stripeTokenId: $stripeTokenId
      discountCode: $discountCode
    ) {
      patientPackage {
        id
        name
        description
        rateCents
        bookableAppointments: bookablePackageItemTypes {
          id
          appointmentTypeId
          name
          description
          virtual
          videoLink
          locations {
            id
            name
            address
          }
        }
        previouslyBookedAppointments {
          startTime
          endTime
          timeZone
          appointmentType {
            id
            virtual
            name
            description
            videoLink
          }
        }
        appointmentPackage: package {
          id
          name
          quantity
        }
      }
    }
  }
`;

export const INTAKE_SAVE_ANSWER = gql`
  mutation createAnswer($formId: Int!, $formElementId: Int!, $data: String, $dataArray: [String!]) {
    createAnswer(
      formId: $formId
      formElementId: $formElementId
      data: $data
      dataArray: $dataArray
    ) {
      answer {
        id
        data
        dataArray
        formElementId
        formElementTitle
        percentageComplete
      }
    }
  }
`;

export const INTAKE_COMPLETE_FORM = gql`
  mutation updateFormCompletion($formId: Int!, $completedAt: String!) {
    updateFormCompletion(formId: $formId, completedAt: $completedAt) {
      formCompletion {
        id
        completedAt
      }
    }
  }
`;

export const UPDATE_FEDERATED_ID = gql`
  mutation updateFederatedId($federatedId: String!) {
    updatePatient(federatedId: $federatedId) {
      patient {
        id
        federatedId
      }
    }
  }
`;

/**
 * Patient Consent Forms
 */
export const PATIENT_CONSENT_SUBMIT = gql`
  mutation createPatientConsent($consentId: Int!) {
    createPatientConsent(consentId: $consentId) {
      patientConsent {
        id
        consent {
          id
          html: htmlLink
          name
        }
        consentId
        consentedAt
      }
    }
  }
`;

/**
 * Period tracking
 */
export const CREATE_PERIOD = gql`
  mutation createPeriod($periodDate: String!) {
    createPeriod(periodDate: $periodDate) {
      period {
        periodDate
        nextPeriodDate
        daysBetweenCycles
        id
      }
    }
  }
`;

export const UPDATE_PERIOD = gql`
  mutation updatePeriod($id: ID!, $periodDate: String!) {
    updatePeriod(id: $id, periodDate: $periodDate) {
      period {
        periodDate
        nextPeriodDate
        daysBetweenCycles
        id
      }
    }
  }
`;

export const UPDATE_CYCLE_LENGTH = gql`
  mutation updateCycleLength($daysBetweenCycles: Int!) {
    updateCycleLength(daysBetweenCycles: $daysBetweenCycles) {
      period {
        periodDate
        nextPeriodDate
        daysBetweenCycles
        id
      }
    }
  }
`;

export const CREATE_PATIENT_ENTERPRISE_MEMBERSHIP = gql`
  mutation createPatientEnterpriseMembership(
    $employerAccessCode: String!
    $employeeAccessCode: String!
  ) {
    createPatientEnterpriseMembership(
      employerAccessCode: $employerAccessCode
      employeeAccessCode: $employeeAccessCode
    ) {
      patientMembership {
        id
      }
    }
  }
`;

export const RECOGNIZE_PATIENT_FROM_EPIC_SSO = gql`
  mutation recognizePatientFromEpicSso($epicIdtoken: String!, $epicAccessToken: String!) {
    recognizePatientFromEpicSso(epicIdtoken: $epicIdtoken, epicAccessToken: $epicAccessToken) {
      resultCode
      errors
      patient {
        id
        email
        firstName
        lastName
      }
    }
  }
`;

// it is the same mutation as above, just different use case
export const CREATE_PATIENT_VIA_EPIC_SSO = gql`
  mutation recognizePatientFromEpicSso(
    $epicIdtoken: String!
    $epicAccessToken: String!
    $newPatientData: EpicNewPatient!
  ) {
    recognizePatientFromEpicSso(
      epicIdtoken: $epicIdtoken
      epicAccessToken: $epicAccessToken
      newPatientData: $newPatientData
    ) {
      resultCode
      errors
      patient {
        id
        email
        firstName
        lastName
      }
    }
  }
`;

export const RECOGNIZE_PATIENT_FROM_MEDTRONIC_SSO = gql`
  mutation recognizePatientFromMedtronicSso($medtronicIdtoken: String!) {
    recognizePatientFromMedtronicSso(medtronicIdtoken: $medtronicIdtoken) {
      resultCode
      errors
      patient {
        id
        email
        firstName
        lastName
      }
    }
  }
`;

// it is the same mutation as above, just different use case
export const CREATE_PATIENT_VIA_MEDTRONIC_SSO = gql`
  mutation recognizePatientFromMedtronicSso(
    $medtronicIdtoken: String!
    $newPatientData: MedtronicNewPatient!
  ) {
    recognizePatientFromMedtronicSso(
      medtronicIdtoken: $medtronicIdtoken
      newPatientData: $newPatientData
    ) {
      resultCode
      errors
      patient {
        id
        email
        firstName
        lastName
      }
    }
  }
`;

export const REGISTER_PATIENT_TO_EVENT = gql`
  mutation registerToEvent($event: String!) {
    registerToEvent(event: $event) {
      event
    }
  }
`;
